import React from "react";

interface StatCardProps {
  icon: React.ComponentType<any>;
  title: string;
  value: number | string;
  subtitle?: string;
  color?: string;
  theme?: "blue" | "green"; // New theme prop
}

export const StatCard = ({
  icon: Icon,
  title,
  value,
  subtitle,
  color,
  theme = "blue", // Default to blue theme
}: StatCardProps) => {
  // Theme configurations with your exact colors
  const themeConfig = {
    blue: {
      background: "bg-white/70",
      border: "border-[#2563EB]/20",
      iconBg: "bg-[#2563EB]/10",
      iconColor: "text-[#2563EB]",
      titleGradient: "from-[#2563EB] to-[#1e40af]",
      valueGradient: "from-slate-900 to-[#2563EB]",
      subtitle: "bg-[#2563EB]/10 text-[#2563EB]",
      hoverBorder: "from-[#2563EB]/30 via-[#2563EB]/40 to-[#2563EB]/30",
      cornerAccent: "bg-[#2563EB]",
      shadow: "shadow-[#2563EB]/10",
    },
    green: {
      background: "bg-white/70",
      border: "border-[#059669]/20",
      iconBg: "bg-[#059669]/10",
      iconColor: "text-[#059669]",
      titleGradient: "from-[#059669] to-[#047857]",
      valueGradient: "from-slate-900 to-[#059669]",
      subtitle: "bg-[#059669]/10 text-[#059669]",
      hoverBorder: "from-[#059669]/30 via-[#059669]/40 to-[#059669]/30",
      cornerAccent: "bg-[#059669]",
      shadow: "shadow-[#059669]/10",
    },
  };

  const currentTheme = themeConfig[theme];

  return (
    <div className="group relative">
      <div
        className={`relative ${currentTheme.background} ${currentTheme.border} border rounded-2xl p-6 shadow-md ${currentTheme.shadow}`}
      >
        <div className="flex items-center justify-between mb-6">
          <div className={`relative rounded-xl p-3 ${currentTheme.iconBg}`}>
            <Icon
              className={`w-6 h-6 ${currentTheme.iconColor} relative z-10`}
            />
          </div>
          <div className="flex items-baseline gap-2">
            <h3
              className={`text-2xl font-bold bg-gradient-to-r ${currentTheme.valueGradient} bg-clip-text text-transparent`}
            >
              {value?.toLocaleString() || "0"}
            </h3>
            <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent"></div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-2">
          <p
            className={`text-sm font-medium bg-gradient-to-r ${currentTheme.titleGradient} bg-clip-text text-transparent`}
          >
            {title}
          </p>

          {subtitle && (
            <p
              className={`text-xs ${currentTheme.subtitle} px-2 py-1 rounded-md inline-block`}
            >
              {subtitle}
            </p>
          )}
        </div>

        {/* Animated border */}
        <div
          className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${currentTheme.hoverBorder} opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10`}
        ></div>

        {/* Corner accent */}
        <div
          className={`absolute top-4 right-4 w-2 h-2 ${currentTheme.cornerAccent} rounded-full opacity-60`}
        ></div>
      </div>
    </div>
  );
};
